import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { formatRent, formatDate, petPolicyLabel, getPhotoUrl } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { walkingTime } from '../../utils/distance';

export default function ListingCard({ listing, saved, onSave, type = 'apartment', onBoost, onEdit, onDelete, onToggleLeased }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [imageIdx, setImageIdx] = useState(0);
  const [leased, setLeased] = useState(listing.leased || false);
  // Only use photos that are real absolute URLs (Cloudinary).
  const photos = (listing.photos || []).filter(p => p && p.startsWith('http'));
  const isOwner = user?.id === listing.landlord_id || user?.id === listing.poster_id;

  const detailPath = (type === 'sublease' || listing._listingType === 'sublease')
    ? `/subleases/${listing.id}`
    : `/listings/${listing.id}`;

  const handleToggleLeased = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const endpoint = type === 'sublease' ? `/subleases/${listing.id}/leased` : `/listings/${listing.id}/leased`;
      const res = await api.patch(endpoint);
      setLeased(res.data.leased);
      if (onToggleLeased) onToggleLeased(listing.id, res.data.leased);
    } catch {}
  };

  const handleMessage = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const ownerId = listing.landlord_id || listing.poster_id;
      const res = await api.post('/messages/start', {
        other_user_id: ownerId,
        listing_id: type === 'apartment' ? listing.id : null,
        sublease_id: type === 'sublease' ? listing.id : null,
        conversation_type: type,
      });
      navigate(`/messages/${res.data.id}`);
    } catch {}
  };

  const isSublease = type === 'sublease' || listing._listingType === 'sublease';

  return (
    <Link
      to={detailPath}
      className="card flex flex-col transition-all duration-200 hover:border-[#CCCCCC] hover:shadow-md"
    >
      {/* Photo */}
      <div className="relative h-48 bg-[#F0F0F0] overflow-hidden">
        {photos.length > 0 ? (
          <>
            <img
              src={getPhotoUrl(photos[imageIdx])}
              alt={listing.property_name || listing.address}
              className="w-full h-full object-cover"
            />
            {photos.length > 1 && (
              <div className="absolute bottom-2 right-2 flex gap-1">
                {photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={e => { e.stopPropagation(); e.preventDefault(); setImageIdx(i); }}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imageIdx ? 'bg-[#C8A800]' : 'bg-white/60'}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#CCCCCC]">
            {isSublease ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-12 h-12">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-12 h-12">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            )}
          </div>
        )}

        {/* Boosted badge */}
        {listing.boosted && (
          <div className="absolute top-8 left-2">
            <span className="badge-gold">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              Boosted
            </span>
          </div>
        )}

        {/* Property type badge */}
        <div className={`absolute top-2 left-2 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm tracking-wide ${
          isSublease ? 'bg-blue-500' : listing.property_type === 'house' ? 'bg-emerald-600' : 'bg-[#1B1B1B]'
        }`}>
          {isSublease ? 'SUBLEASE' : listing.property_type === 'house' ? 'HOUSE' : 'APARTMENT'}
        </div>

        {/* Leased sticker */}
        {leased && (
          <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md tracking-wide">
            LEASED
          </div>
        )}

        {/* Save button */}
        {!isOwner && !leased && (
          <button
            onClick={e => { e.stopPropagation(); e.preventDefault(); onSave(); }}
            className="absolute top-2 right-2 w-8 h-8 bg-white/90 border border-[#E8E8E8] backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-sm"
          >
            {saved ? (
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-red-500">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4 text-[#AAAAAA]">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col gap-3">
        <div>
          {!isSublease && (
            <h3 className="font-bold text-[#1B1B1B] text-lg leading-tight">{listing.property_name}</h3>
          )}
          <p className="nav-link text-sm text-[#777777] mt-0.5">{listing.address}, {listing.city}</p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-y-1">
          <div>
            <span className="text-2xl font-black text-[#1B1B1B]">{formatRent(Math.ceil(listing.monthly_rent * 1.015))}</span>
            <span className="nav-link text-xs text-[#AAAAAA] ml-1.5">/ mo</span>
            <p className="nav-link text-xs text-[#AAAAAA] mt-0.5">Listed at {formatRent(listing.monthly_rent)} + 1.5% fee</p>
          </div>
          <div className="flex items-center gap-3 nav-link text-sm text-[#777777]">
            <span className="flex items-center gap-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 inline">
                <path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/>
              </svg>
              {listing.bedrooms} bd
            </span>
            {listing.bathrooms && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 inline">
                  <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/>
                  <line x1="10" y1="5" x2="8" y2="7"/><line x1="2" y1="12" x2="22" y2="12"/>
                </svg>
                {listing.bathrooms} ba
              </span>
            )}
          </div>
        </div>

        {walkingTime(listing.latitude, listing.longitude) && (
          <div className="flex items-center gap-1 nav-link text-xs text-[#555555]">
            <span>🚶</span>
            <span>{walkingTime(listing.latitude, listing.longitude)} to campus</span>
          </div>
        )}

        {isSublease && (
          <div className="flex items-center gap-1.5 nav-link text-sm text-[#777777]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 inline flex-shrink-0">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {formatDate(listing.sublease_start)} – {formatDate(listing.sublease_end)}
          </div>
        )}

        {!isSublease && listing.available_date && (
          <div className="flex items-center gap-1.5 nav-link text-sm text-[#777777]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 inline flex-shrink-0">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Available {formatDate(listing.available_date)}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {listing.pet_policy && (
            <span className="badge-gray">{petPolicyLabel(listing.pet_policy)}</span>
          )}
          {listing.utilities_included?.map(u => (
            <span key={u} className="badge-gray capitalize">{u}</span>
          ))}
        </div>

        {listing.description && (
          <p className="nav-link text-sm text-[#777777] line-clamp-2">{listing.description}</p>
        )}

        {/* Landlord/Poster info */}
        <div className="flex items-center gap-2 nav-link text-sm text-[#777777] mt-auto">
          {(listing.landlord_avatar || listing.poster_avatar) ? (
            <img src={getPhotoUrl(listing.landlord_avatar || listing.poster_avatar)} alt="" className="w-5 h-5 rounded-full object-cover" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-[#F0F0F0] flex items-center justify-center text-xs text-[#AAAAAA]">
              {(listing.landlord_name || listing.poster_name)?.[0]}
            </div>
          )}
          <span>{listing.landlord_name || listing.poster_name}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          {!isOwner ? (
            <button onClick={handleMessage} className="btn-primary flex-1 py-2 text-sm">
              Message
            </button>
          ) : (
            <div className="flex flex-wrap gap-2 w-full">
              {onBoost && !listing.boosted && !leased && (
                <button
                  onClick={e => { e.stopPropagation(); e.preventDefault(); onBoost(listing.id); }}
                  className="btn-secondary flex-1 py-2 text-sm"
                >
                  Boost — $25
                </button>
              )}
              <button
                onClick={handleToggleLeased}
                className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-colors ${leased ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100' : 'border-[#DDDDDD] text-[#555555] hover:border-green-400 hover:text-green-600'}`}
              >
                {leased ? '✓ Leased' : 'Mark Leased'}
              </button>
              {onEdit && (
                <button
                  onClick={e => { e.stopPropagation(); e.preventDefault(); onEdit(); }}
                  className="btn-ghost py-2 px-3 text-sm border border-[#DDDDDD]"
                >
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={e => { e.stopPropagation(); e.preventDefault(); onDelete(); }}
                  className="py-2 px-3 text-sm rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
